import type { ComponentType, HTMLAttributes, RefAttributes } from "react";

export interface AnimatedIconHandle {
  startAnimation: () => Promise<unknown>;
  stopAnimation: () => void;
}

export type AnimatedIconComponent = ComponentType<
  HTMLAttributes<HTMLSpanElement> & { size?: number } & RefAttributes<AnimatedIconHandle>
>;

type Loader = () => Promise<AnimatedIconComponent>;

export const ANIMATED_ICON_LOADERS: Record<string, Loader> = {
  "activity": () => import("./activity").then((m) => m.ActivityIcon),
  "bell": () => import("./bell").then((m) => m.BellIcon),
  "brain": () => import("./brain").then((m) => m.BrainIcon),
  "chart-line": () => import("./chart-line").then((m) => m.ChartLineIcon),
  "chart-pie": () => import("./chart-pie").then((m) => m.ChartPieIcon),
  "check": () => import("./check").then((m) => m.CheckIcon),
  "circle-check": () => import("./circle-check").then((m) => m.CircleCheckIcon),
  "circle-dollar-sign": () => import("./circle-dollar-sign").then((m) => m.CircleDollarSignIcon),
  "circle-help": () => import("./circle-help").then((m) => m.CircleHelpIcon),
  "clock": () => import("./clock").then((m) => m.ClockIcon),
  "cpu": () => import("./cpu").then((m) => m.CpuIcon),
  "dollar-sign": () => import("./dollar-sign").then((m) => m.DollarSignIcon),
  "euro": () => import("./euro").then((m) => m.EuroIcon),
  "eye": () => import("./eye").then((m) => m.EyeIcon),
  "eye-off": () => import("./eye-off").then((m) => m.EyeOffIcon),
  "file-text": () => import("./file-text").then((m) => m.FileTextIcon),
  "flame": () => import("./flame").then((m) => m.FlameIcon),
  "gauge": () => import("./gauge").then((m) => m.GaugeIcon),
  "git-commit-horizontal": () => import("./git-commit-horizontal").then((m) => m.GitCommitHorizontalIcon),
  "git-fork": () => import("./git-fork").then((m) => m.GitForkIcon),
  "graduation-cap": () => import("./graduation-cap").then((m) => m.GraduationCapIcon),
  "heart": () => import("./heart").then((m) => m.HeartIcon),
  "hourglass": () => import("./hourglass").then((m) => m.HourglassIcon),
  "key": () => import("./key").then((m) => m.KeyIcon),
  "languages": () => import("./languages").then((m) => m.LanguagesIcon),
  "layout-grid": () => import("./layout-grid").then((m) => m.LayoutGridIcon),
  "lock": () => import("./lock").then((m) => m.LockIcon),
  "message-circle": () => import("./message-circle").then((m) => m.MessageCircleIcon),
  "message-square": () => import("./message-square").then((m) => m.MessageSquareIcon),
  "moon": () => import("./moon").then((m) => m.MoonIcon),
  "plus": () => import("./plus").then((m) => m.PlusIcon),
  "rocket": () => import("./rocket").then((m) => m.RocketIcon),
  "search": () => import("./search").then((m) => m.SearchIcon),
  "settings": () => import("./settings").then((m) => m.SettingsIcon),
  "shield-check": () => import("./shield-check").then((m) => m.ShieldCheckIcon),
  "sparkles": () => import("./sparkles").then((m) => m.SparklesIcon),
  "thermometer": () => import("./thermometer").then((m) => m.ThermometerIcon),
  "timer": () => import("./timer").then((m) => m.TimerIcon),
  "trending-down": () => import("./trending-down").then((m) => m.TrendingDownIcon),
  "trending-up": () => import("./trending-up").then((m) => m.TrendingUpIcon),
  "upload": () => import("./upload").then((m) => m.UploadIcon),
  "user-plus": () => import("./user-plus").then((m) => m.UserPlusIcon),
  "users": () => import("./users").then((m) => m.UsersIcon),
  "wind": () => import("./wind").then((m) => m.WindIcon),
  "workflow": () => import("./workflow").then((m) => m.WorkflowIcon),
  "zap": () => import("./zap").then((m) => m.ZapIcon),
};

export const ANIMATED_ICON_ALIASES: Record<string, string> = {
  "check-circle-2": "circle-check",
  "circle-question-mark": "circle-help",
  "git-commit": "git-commit-horizontal",
  "help-circle": "circle-help",
  "line-chart": "chart-line",
  "pie-chart": "chart-pie",
  "stars": "sparkles",
};

export function resolveAnimatedIconName(name: string): string | null {
  if (name in ANIMATED_ICON_LOADERS) return name;
  const alias = ANIMATED_ICON_ALIASES[name];
  return alias && alias in ANIMATED_ICON_LOADERS ? alias : null;
}
